import React, { useState, useEffect } from "react";
import Datatable from "../../../components/shared/datatable/Datatable";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { DeleteSvg } from "../../../svgFiles/DeleteSvg";
import SupplierModal from "./SupplierModal";
import {
  useRemoveSupplierMutation,
  useSearchSuppliersQuery,
} from "./supplierApi";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import CommonDeleteModal from "../../../components/shared/modalLayout/CommonDeleteModal";
import { convertToUserTimezone, showToast, ucwords } from "../../../helpers/Utils";
import useTranslator from "../../../hooks/useTranslator";

const Supplier = () => {
  const { _t } = useTranslator();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const {
    data: suppliers,
    isLoading,
    isError,
    error,
  } = useSearchSuppliersQuery({ page, pageSize });
  const [removeSupplier, { isLoading: isDeleting }] =
    useRemoveSupplierMutation();

  useEffect(() => {
    if (isError) {
      showToast(error.data.message || "Failed to fetch suppliers");
    }
  }, [isError, error]);

  const handleAddSupplier = () => {
    setModalMode("add");
    setSelectedSupplier(null);
    setShowModal(true);
  };

  const handleEdit = (supplier) => {
    setModalMode("edit");
    setSelectedSupplier(supplier);
    setShowModal(true);
  };

  const handleDelete = (supplier) => {
    setSupplierToDelete(supplier);
    setShowConfirmationModal(true);
  };

  const confirmDelete = async () => {
    setShowConfirmationModal(false);
    try {
      if (supplierToDelete && supplierToDelete.id) {
        await removeSupplier(supplierToDelete.id).unwrap();
      } else {
        showToast("Please select a supplier first.");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      showToast(
        error?.data?.message || "Error deleting supplier. Please try again."
      );
    } finally {
      setSupplierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
    setSupplierToDelete(null);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const columns = [
    { 
      label: _t("date", "Date"),
      accessor: "date_of_joining",
      cell: ({ value }) => value ? convertToUserTimezone(value) : "" 
    },
    { label: _t("name", "Name"), accessor: "name" },
    // { label: _t("email", "Email"), accessor: "email" },
    { label: _t("company_name", "Company Name"), accessor: "company_name" },
    { label: _t("phone_no", "Phone No"), accessor: "mobile", cell: ({ value }) => value ?? "N/A"  },
    { label: _t("role", "Role"), accessor: "role_name", cell: ({ value }) => ucwords(value) ?? "N/A"  },
    
    // { label: "Address", accessor: "address" },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => (
        <span className={value === 1 ? "cmn_badge" : "cmn_badge inactive"}>
          {value === 1 ? _t("active", "Active") : _t("inactive", "Inactive")}
        </span>
      ),
    },
    {
      label: _t("edit_delete", "Edit/Delete"),
      accessor: "actions",
      cell: ({ row }) => (
        <>
          <span className="cursor-pointer pe-2" onClick={() => handleEdit(row)}>
            <EditSvg />
          </span>
          <span className="cursor-pointer" onClick={() => handleDelete(row)}>
            {DeleteSvg}
          </span>
        </>
      ),
    },
  ];

  return (
    <div>
      <SupplierModal
        show={showModal}
        setShow={setShowModal}
        mode={modalMode}
        supplier={selectedSupplier}
      />

      <CommonDeleteModal // Add ConfirmationModal here
        show={showConfirmationModal}
        handleClose={cancelDelete}
        title={_t("confirm_delete", "Confirm Delete")}
        message={`${_t(
          "sure_confirm_delete",
          "Are you sure you want to delete"
        )} ${supplierToDelete?.name}?`}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />      
        <Datatable
          title={_t("suppliers", "Suppliers")}
          columns={columns}
          tableData={suppliers?.data}
          buttons={[
            {
              label: _t("add_supplier", "Add Supplier"),
              onClick: handleAddSupplier,
              icon: PlusSvg,
            },
          ]}
          pagination
          page={page}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
    </div>
  );
};

export default Supplier;
